/**
 * The route a dashboard Sentry event happened on.
 *
 * WHY THIS EXISTS — every dashboard issue carries `transaction: /`
 * -----------------------------------------------------------------
 * The dashboard routes with `HashRouter`, so the route lives in the URL
 * FRAGMENT (`https://dashboard.…/#/admin/gameDetails?game=12`). The fragment is
 * never sent anywhere and `browserTracingIntegration()` names its transactions
 * from `location.pathname`, which is `/` on every page of this app. Measured on
 * live events (ledger run 11, re-confirmed run 24): **all twelve dashboard
 * issues in the backlog carry `transaction: /` and `culprit: /`** — which is why
 * every dashboard item in the ledger can only be described by its message text,
 * and why DASH-3N, DASHBOARD-1P, DASHBOARD-14 and DASHBOARD-3P have sat
 * un-triageable for 70+ runs.
 *
 * This module derives the route from the fragment and puts it on the event.
 *
 * DELIBERATE SCOPE — errors only.
 * `beforeSend` runs for error events; transactions go through
 * `beforeSendTransaction`, which is left alone ON PURPOSE. The four open
 * dashboard PERFORMANCE issues (`Large HTTP payload`, `Large Render Blocking
 * Asset`, `N+1 API Call`) are grouped by their transaction name, so renaming
 * transactions would re-group live issues; naming errors costs nothing that is
 * already grouped, because an exception groups on its stack trace and the
 * `ApiError` path sets an explicit `fingerprint` (see `apiErrorReporting.ts`).
 *
 * A Sentry React Router v6 integration would name transactions properly, but it
 * is exactly the change this scope excludes, and it needs the router's own
 * hooks wired into `App.tsx`. This is the half that pays for itself today.
 */
import { templateEndpoint } from './apiErrorReporting';

/** What a route resolves to when there is no hash route to read. */
export const UNKNOWN_ROUTE = '/';

/**
 * Upper bound on the string we hand Sentry. A fragment is caller-controlled and
 * unbounded; Sentry would truncate it anyway, but a tag value that varies only
 * past its truncation point is worse than one that is visibly capped.
 */
export const MAX_ROUTE_LENGTH = 200;

/**
 * `#/admin/court-recording/42?tab=clips` → `/admin/court-recording/:id`.
 *
 * Variable segments are collapsed by `templateEndpoint`, the SAME helper that
 * templates API endpoints for fingerprints — deliberately shared so a route and
 * the call it makes describe an id the same way, and so the conservative rule
 * that a word merely containing digits (`v2`) is NOT an id has one definition.
 *
 * Anything that is not a hash ROUTE — an empty hash, a bare `#`, an in-page
 * anchor such as `#section` — resolves to `UNKNOWN_ROUTE` rather than being
 * coerced into a route that does not exist. `HashRouter` always writes `#/…`.
 *
 * Total function: never throws, for any input. It runs inside `beforeSend`, and
 * an instrumentation helper that throws there costs the event it was decorating.
 */
export function normalizeHashRoute(hash: unknown): string {
    try {
        if (typeof hash !== 'string') return UNKNOWN_ROUTE;

        const trimmed = hash.trim();
        if (!trimmed.startsWith('#/')) return UNKNOWN_ROUTE;

        // Drop the leading '#', then any nested fragment ('#/courts#top').
        // `templateEndpoint` drops the query string itself.
        const path = trimmed.slice(1).split('#')[0];
        const templated = templateEndpoint(path);
        if (templated === UNKNOWN_ROUTE) return UNKNOWN_ROUTE;

        // `templateEndpoint` strips only the OUTER slashes, so '//admin//courts'
        // survives as 'admin//courts'; an empty segment is not a route segment.
        const route = `/${templated}`.replace(/\/{2,}/g, '/');

        return route.length > MAX_ROUTE_LENGTH
            ? route.slice(0, MAX_ROUTE_LENGTH)
            : route;
    } catch {
        return UNKNOWN_ROUTE;
    }
}

/**
 * The subset of a Sentry event this module touches.
 *
 * `tags` is deliberately `unknown`-valued rather than a hand-written union of
 * primitives: Sentry's own `Primitive` includes `bigint` and `symbol`, so a
 * narrower union makes `ErrorEvent` fail this constraint, the generic below
 * silently falls back to THIS interface as its return type, and `beforeSend`
 * stops type-checking. Caught by `npm run build:prod` — `npx tsc --noEmit` in
 * this project passed it (protocol §5: gate the dashboard on the deploy build).
 */
export interface RouteAnnotatableEvent {
    transaction?: string;
    tags?: Record<string, unknown>;
}

/** The subset of `window.location` this module reads. */
export interface RouteSource {
    hash?: unknown;
    pathname?: unknown;
}

/**
 * Names the event's route, and returns the SAME event object.
 *
 * Two things, of which only the first can ever be lost:
 *
 * 1. `event.transaction` is overwritten **only when it carries no route
 *    information** — i.e. it is absent, `/`, or the document pathname, which is
 *    what `browserTracingIntegration()` produces on a hash-routed app. If some
 *    later change (a React Router integration) starts naming transactions
 *    properly, that name wins and this helper does not fight it.
 * 2. The `dashboard.route` TAG is always set. A tag is not part of grouping, so
 *    it is the half that cannot disturb an existing issue, and it is what makes
 *    `dashboard.route:"/admin/gameDetails"` searchable in Sentry.
 *
 * Never throws and never returns `null`: a `beforeSend` that throws or returns
 * `null` DROPS the event, which would turn a triage improvement into data loss.
 */
export function applyRouteToEvent<T extends RouteAnnotatableEvent>(
    event: T,
    location: RouteSource | undefined | null,
): T {
    try {
        const route = normalizeHashRoute(location?.hash);

        event.tags = { ...event.tags, 'dashboard.route': route };

        const current = event.transaction;
        const pathname =
            typeof location?.pathname === 'string' ? location.pathname : undefined;
        const carriesNoRoute =
            current === undefined ||
            current === null ||
            current === '' ||
            current === UNKNOWN_ROUTE ||
            (pathname !== undefined && current === pathname);

        if (carriesNoRoute) event.transaction = route;
    } catch {
        // An event with no route is worth more than no event at all.
    }

    return event;
}
